import { StrategyBuilderFunction, EMPTY_STRATEGY } from 'utils/shared/optionProfitCalculator/types';
import { addLongPut, addShortCall } from 'utils/shared/optionProfitCalculator/addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  DEFAULT_STOCK_POSITION,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from 'utils/shared/optionProfitCalculator/utils';

export const collar: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strike,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  // Use the default expiration date function to get the expiration date
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // For the put option, choose a strike price slightly below the current spot price
  const putStrikePrice = strike
    ? strike
    : findClosestStrikePrice(strikesAtExpiration, getStrikeDistance({ price: spot, unit: -2 }));

  // For the call option, choose a strike price slightly above the current spot price
  const callStrikePrice = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 2 }),
  );

  if (putStrikePrice === -1 || callStrikePrice === -1) {
    return EMPTY_STRATEGY;
  }

  // Build the put option position
  const putOptionPosition = addLongPut({
    strike: putStrikePrice,
    premium: getPremium({
      ask: strikesAtExpiration?.c?.[putStrikePrice]?.p?.a || 0,
      bid: strikesAtExpiration?.c?.[putStrikePrice]?.p?.b || 0,
      strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
    }),
    expirationDate: expirationDateForOptions,
    expirationKey: expirationKeyForOptions,
    numberOfContracts: 1 * contractMultiplier,
  });

  // Build the call option position
  const callOptionPosition = addShortCall({
    strike: callStrikePrice,
    premium: getPremium({
      ask: strikesAtExpiration?.c?.[callStrikePrice]?.c?.a || 0,
      bid: strikesAtExpiration?.c?.[callStrikePrice]?.c?.b || 0,
      strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
    }),
    expirationDate: expirationDateForOptions,
    expirationKey: expirationKeyForOptions,
    numberOfContracts: 1 * contractMultiplier,
  });

  // Stock position - 100 shares per strategy unit at the current market price
  const stockPosition = {
    price: spot,
    quantity: DEFAULT_STOCK_POSITION * contractMultiplier, // 100 shares per strategy unit
  };

  return {
    optionPositions: [putOptionPosition, callOptionPosition],
    stockPositions: [stockPosition],
  };
};
